/**
 * Tiny string-based GSAP timeline emitter.
 *
 * Hyperframes runs the composition in a real browser context, so the
 * "timeline" is just literal JS that Hyperframes seeks. We build the JS
 * here, in Node, and inject it into the generated index.html.
 *
 * Each helper returns a single line of valid JS that appends a tween to
 * the project-wide paused timeline (`tl`).
 */

export function toJsLiteral(value: unknown): string {
  if (value === undefined) return "undefined";
  if (value === null) return "null";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "null";
    return String(value);
  }
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "string") {
    // Honor "raw:" prefix for callbacks / GSAP method values like '"power2.out"'
    if (value.startsWith("raw:")) return value.slice(4);
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(toJsLiteral).join(",")}]`;
  }
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    const parts = Object.entries(o).map(([k, v]) => `${JSON.stringify(k)}:${toJsLiteral(v)}`);
    return `{${parts.join(",")}}`;
  }
  return JSON.stringify(value);
}

type TweenProps = Record<string, unknown>;

function tweenArgs(target: string, props: TweenProps, atSec: number): string {
  return `${JSON.stringify(target)},${toJsLiteral(props)},${Number(atSec.toFixed(4))}`;
}

function tweenFromToArgs(
  target: string,
  fromProps: TweenProps,
  toProps: TweenProps,
  atSec: number,
): string {
  return `${JSON.stringify(target)},${toJsLiteral(fromProps)},${toJsLiteral(toProps)},${Number(
    atSec.toFixed(4),
  )}`;
}

export const tl = {
  set(target: string, props: TweenProps, atSec: number): string {
    return `tl.set(${tweenArgs(target, props, atSec)});`;
  },
  to(target: string, props: TweenProps, atSec: number): string {
    return `tl.to(${tweenArgs(target, props, atSec)});`;
  },
  from(target: string, props: TweenProps, atSec: number): string {
    return `tl.from(${tweenArgs(target, props, atSec)});`;
  },
  fromTo(target: string, fromProps: TweenProps, toProps: TweenProps, atSec: number): string {
    return `tl.fromTo(${tweenFromToArgs(target, fromProps, toProps, atSec)});`;
  },
  /** Insert raw JS at the given (sorted) position in the script. */
  raw(line: string): string {
    return line;
  },
};

/**
 * Convenience: a "spring-like" enter animation (opacity 0→1, y 28→0) that
 * we use in many places. Replaces Remotion `spring(...)` calls in primitives.
 */
export function enterFromBelow(
  target: string,
  atSec: number,
  opts: { duration?: number; yPx?: number; ease?: string } = {},
): string[] {
  const d = opts.duration ?? 0.5;
  const y = opts.yPx ?? 28;
  const ease = opts.ease ?? "power2.out";
  return [
    tl.fromTo(
      target,
      { opacity: 0, y },
      { opacity: 1, y: 0, duration: d, ease },
      atSec,
    ),
  ];
}

export function fadeIn(target: string, atSec: number, duration = 0.4, ease = "power1.out"): string {
  return tl.fromTo(target, { opacity: 0 }, { opacity: 1, duration, ease }, atSec);
}

export function fadeOut(target: string, atSec: number, duration = 0.4, ease = "power1.in"): string {
  return tl.to(target, { opacity: 0, duration, ease }, atSec);
}
